/**
 * Single visitor's full detail: identity, attribution, raw params,
 * sessions, event timeline, linked leads.
 */

import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  Chip,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {useQuery} from '@apollo/client';
import {PILOT_VISITOR_DETAIL} from '../../graphql/pilotDashboardQueries';

interface Props {
  visitorId: string;
  onBack: () => void;
}

function fmt(iso?: string | null): string {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return iso;
  return new Date(t).toLocaleString();
}

export default function PilotVisitorDetail({visitorId, onBack}: Props) {
  const {data, loading, error} = useQuery(PILOT_VISITOR_DETAIL, {
    variables: {id: visitorId},
    fetchPolicy: 'cache-and-network',
  });

  const visitor = data?.pilotVisitor;

  if (loading && !visitor) {
    return (
      <Box sx={{display: 'flex', justifyContent: 'center', py: 6}}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{mb: 2}}>
        {error.message}
      </Alert>
    );
  }

  if (!visitor) {
    return (
      <Alert severity="info" sx={{mb: 2}}>
        Visitor not found.
      </Alert>
    );
  }

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={onBack}
        size="small"
        sx={{mb: 2}}
      >
        Back to visitors
      </Button>

      <Stack
        direction={{xs: 'column', md: 'row'}}
        spacing={2}
        sx={{mb: 3}}
      >
        <Paper variant="outlined" sx={{flex: 2, p: 3}}>
          <Typography variant="caption" color="text.secondary">
            Visitor identity
          </Typography>
          <Typography variant="h6" sx={{fontWeight: 600, mt: 0.5}}>
            {visitor.identity.email || '— anonymous —'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {[visitor.identity.firstName, visitor.identity.lastName]
              .filter(Boolean)
              .join(' ') || '—'}
            {visitor.identity.title ? ` · ${visitor.identity.title}` : ''}
            {visitor.identity.company ? ` · ${visitor.identity.company}` : ''}
          </Typography>
          {visitor.identity.linkedinUrl && (
            <Typography variant="body2" sx={{mt: 1}}>
              <a
                href={visitor.identity.linkedinUrl}
                target="_blank"
                rel="noreferrer"
              >
                {visitor.identity.linkedinUrl}
              </a>
            </Typography>
          )}
          {visitor.identity.phone && (
            <Typography variant="body2">{visitor.identity.phone}</Typography>
          )}
          <Divider sx={{my: 2}} />
          <Stack direction="row" flexWrap="wrap" gap={1}>
            <Chip
              size="small"
              label={`Source: ${visitor.attribution.source}`}
            />
            {visitor.attribution.campaign && (
              <Chip
                size="small"
                label={`Campaign: ${visitor.attribution.campaign}`}
              />
            )}
            {visitor.attribution.medium && (
              <Chip
                size="small"
                label={`Medium: ${visitor.attribution.medium}`}
              />
            )}
            {visitor.attribution.apolloContactId && (
              <Chip
                size="small"
                label={`Apollo contact: ${visitor.attribution.apolloContactId}`}
              />
            )}
            {visitor.attribution.apolloCampaignId && (
              <Chip
                size="small"
                label={`Apollo campaign: ${visitor.attribution.apolloCampaignId}`}
              />
            )}
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{flex: 1, p: 3}}>
          <Typography variant="caption" color="text.secondary">
            Activity
          </Typography>
          <Stack spacing={1.25} sx={{mt: 1.5}}>
            <Stat label="Stage" value={visitor.stage} />
            <Stat label="First seen" value={fmt(visitor.firstSeenAt)} />
            <Stat label="Last seen" value={fmt(visitor.lastSeenAt)} />
            <Stat label="Identified" value={fmt(visitor.identifiedAt)} />
            <Stat label="Sessions" value={visitor.sessionCount} />
            <Stat label="Events" value={visitor.eventCount} />
            <Stat label="Leads" value={visitor.leadCount} />
          </Stack>
        </Paper>
      </Stack>

      {visitor.rawParams && Object.keys(visitor.rawParams).length > 0 && (
        <Accordion sx={{mb: 3}} variant="outlined">
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">
              Raw URL params ({Object.keys(visitor.rawParams).length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box
              component="pre"
              sx={{
                fontSize: 12,
                fontFamily: 'monospace',
                background: 'rgba(0,0,0,0.03)',
                p: 2,
                borderRadius: 1,
                overflow: 'auto',
                m: 0,
              }}
            >
              {JSON.stringify(visitor.rawParams, null, 2)}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {visitor.leads && visitor.leads.length > 0 && (
        <Paper variant="outlined" sx={{mb: 3, p: 3}}>
          <Typography variant="subtitle2" sx={{fontWeight: 600, mb: 2}}>
            Linked leads ({visitor.leads.length})
          </Typography>
          <Stack spacing={2}>
            {visitor.leads.map(
              (lead: {
                id: string;
                kind: string;
                email: string;
                createdAt: string;
                notes?: string | null;
                roiPayload?: unknown;
                simPayload?: unknown;
              }) => (
                <Box
                  key={lead.id}
                  sx={{
                    border: '1px solid rgba(0,0,0,0.08)',
                    borderRadius: 1,
                    p: 2,
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                  >
                    <Box>
                      <Chip
                        size="small"
                        label={lead.kind}
                        color="success"
                      />
                      <Typography variant="body2" sx={{mt: 1}}>
                        {lead.email}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {fmt(lead.createdAt)}
                    </Typography>
                  </Stack>
                  {lead.notes && (
                    <Typography
                      variant="body2"
                      sx={{mt: 1, color: 'text.secondary'}}
                    >
                      {lead.notes}
                    </Typography>
                  )}
                  {Boolean(lead.roiPayload || lead.simPayload) && (
                    <Accordion sx={{mt: 1, boxShadow: 'none'}}>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{p: 0, minHeight: 32}}
                      >
                        <Typography variant="caption" color="text.secondary">
                          Widget payload
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{p: 0}}>
                        <Box
                          component="pre"
                          sx={{
                            fontSize: 11,
                            fontFamily: 'monospace',
                            background: 'rgba(0,0,0,0.03)',
                            p: 1.5,
                            borderRadius: 1,
                            overflow: 'auto',
                            m: 0,
                          }}
                        >
                          {JSON.stringify(
                            {
                              roiPayload: lead.roiPayload,
                              simPayload: lead.simPayload,
                            },
                            null,
                            2,
                          )}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  )}
                </Box>
              ),
            )}
          </Stack>
        </Paper>
      )}

      <Paper variant="outlined" sx={{p: 3}}>
        <Typography variant="subtitle2" sx={{fontWeight: 600, mb: 2}}>
          Event timeline ({visitor.events.length})
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>When</TableCell>
                <TableCell>Event</TableCell>
                <TableCell>Session</TableCell>
                <TableCell>Payload</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visitor.events.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{py: 3}}>
                    <Typography variant="body2" color="text.secondary">
                      No events captured yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {visitor.events.map(
                (event: {
                  id: string;
                  name: string;
                  sessionId: string;
                  occurredAt: string;
                  payload?: Record<string, unknown> | null;
                }) => (
                  <TableRow key={event.id}>
                    <TableCell sx={{whiteSpace: 'nowrap'}}>
                      <Typography variant="caption">
                        {fmt(event.occurredAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{fontFamily: 'monospace'}}
                      >
                        {event.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{fontFamily: 'monospace'}}
                      >
                        {event.sessionId.slice(0, 8)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {event.payload &&
                      Object.keys(event.payload).length > 0 ? (
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: 'monospace',
                            color: 'text.secondary',
                          }}
                        >
                          {JSON.stringify(event.payload) as string}
                        </Typography>
                      ) : (
                        <Typography
                          variant="caption"
                          color="text.disabled"
                        >
                          —
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ),
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}

function Stat({label, value}: {label: string; value: React.ReactNode}) {
  return (
    <Stack direction="row" justifyContent="space-between">
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{fontWeight: 500}}>
        {value}
      </Typography>
    </Stack>
  );
}
